import React, { useState, useEffect, useRef, useCallback } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';
import { AutoComplete, Input, Typography, Button } from 'antd';

import { ModalMap } from '../../views/ServicesHistoryDetails/components/tabs/ModalMap';
import { useDictionaryTranslation } from '../../hooks/useDictionaryTranslation';
import { debounceFunction } from '../../utility/utility';

const AutocompleteInput = ({
  label,
  value,
  onChange,
  readOnly,
  extendedFormEnabled,
  placeholder = 'addressPlaceholder',
}) => {
  const { Text } = Typography;
  const { translate: t } = useDictionaryTranslation();
  const [displayedValue, setDisplayedValue] = useState(value?.address);
  const [suggestions, setSuggestions] = useState([]);

  const places = useMapsLibrary('places');
  const sessionTokenRef = useRef(null);
  const [showModal, setShowModal] = useState(false);

  const onShowModal = () => setShowModal(true);
  const onCloseModal = () => setShowModal(false);

  useEffect(() => {
    setDisplayedValue(value?.address);
  }, [value]);

  if (readOnly) {
    return (
      <Text>
        <b>{label}:</b>
        {extendedFormEnabled ? <br /> : null}
        <Text>{value?.address}</Text>
        {value?.markerCoords != null ? (
          <>
            <Button style={{ padding: 0 }} type="link" onClick={onShowModal}>
              {t('showLocation')}
            </Button>
          </>
        ) : null}
        {showModal ? <ModalMap show={showModal} handleClose={onCloseModal} coordsInfo={{ value }} /> : null}
      </Text>
    );
  }

  const searchValue = useCallback(
    debounceFunction(async (searchText) => {
      if (!searchText || !places?.AutocompleteSuggestion) {
        setSuggestions([]);
        return;
      }

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new places.AutocompleteSessionToken();
      }

      try {
        const { suggestions: results } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: searchText,
          sessionToken: sessionTokenRef.current,
        });

        setSuggestions(
          results.map((s) => ({
            value: s.placePrediction.text.toString(),
            label: s.placePrediction.text.toString(),
            suggestion: s,
          })),
        );
      } catch {
        setSuggestions([]);
      }
    }, 300),
    [places],
  );

  const onSearch = async (searchText) => {
    setDisplayedValue(searchText);
    searchValue(searchText);
  };

  const onSelect = async (selectedValue, option) => {
    if (!option.suggestion) return;

    const place = option.suggestion.placePrediction.toPlace();
    await place.fetchFields({ fields: ['location', 'formattedAddress'] });
    sessionTokenRef.current = null;

    onChange({
      address: place.formattedAddress,
      markerCoords: {
        latitude: place.location?.lat(),
        longitude: place.location?.lng(),
      },
    });
  };

  const onBlur = () => {
    setDisplayedValue(value?.address);
    setSuggestions([]);
  };

  const onFocus = () => {
    setDisplayedValue('');
  };

  return (
    <AutoComplete
      value={displayedValue}
      options={suggestions}
      onSearch={onSearch}
      onSelect={onSelect}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <Input placeholder={t(placeholder)} />
    </AutoComplete>
  );
};

export const GooglePlacesAutocomplete = ({ label, value, onChange, readOnly, extendedFormEnabled, placeholder }) => {
  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY} libraries={['places']}>
      <AutocompleteInput
        label={label}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        extendedFormEnabled={extendedFormEnabled}
        placeholder={placeholder}
      />
    </APIProvider>
  );
};

AutocompleteInput.propTypes = {
  onChange: PropTypes.func,
  label: PropTypes.string,
  value: PropTypes.object,
  readOnly: PropTypes.bool,
  extendedFormEnabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

GooglePlacesAutocomplete.propTypes = {
  onChange: PropTypes.func,
  label: PropTypes.string,
  value: PropTypes.object,
  readOnly: PropTypes.bool,
  extendedFormEnabled: PropTypes.bool,
  placeholder: PropTypes.string,
};
